import { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cards from "../../components/Cards";
import { FaFileInvoiceDollar, FaUsers, FaBed } from "react-icons/fa";
import { TbSortAscending } from "react-icons/tb";
import { useAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";
import { TiArrowRight } from "react-icons/ti";
import TopDoctorCard from "../../components/TopDoctorCard";
import HospitalBedChart from "../../components/HospitalBedChart";
import HospitalPerformanceChart from "../../components/HospitalPerformanceChart";
import TechnicalSupportNotice from "../../components/TechnicalSupportNotice";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const HospitalDashboard = () => {
  const [dashboardData, setDashboardData] = useState([]);
  const [doctorsData, setDoctorsData] = useState([]); 
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billsLoading, setBillsLoading] = useState(true);
  const [totalBeds, setTotalBeds] = useState(0);
  const [occupiedBeds, setOccupiedBeds] = useState(0);
  const [recentHospitalBills, setRecentHospitalBills] = useState([]);

 useEffect(() => {
    const loadPathologistData = async () => {
      setLoading(true);
      try {
        const api = axios.create({ baseURL: BASE_URL, headers: { "x-api-key": API_KEY } });

        const today = new Date().toISOString().split("T")[0];
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const monthPadded = String(month).padStart(2, '0');

        const [
          doctorsRes,
          todayHospitalBillsRes,
          monthHospitalBillsRes,
          yearHospitalBillsRes,
          hospitalAdmissionsRes,
          hospitalBedsRes,
          recentHospitalBillsRes
        ] = await Promise.all([
          api.get(`/doctor`).catch(() => ({ data: [] })),
          api.get(`/hospital/hospital-bills/bills/range?startDate=${today}&endDate=${today}`).catch(() => ({ data: { totalTransactions: 0, totalAmount: 0 } })),
          api.get(`/hospital/hospital-bills/bills/monthly?year=${year}&month=${monthPadded}`).catch(() => ({ data: { bills: [], totalAmount: 0 } })),
          api.get(`/hospital/hospital-bills/bills/yearly?year=${year}`).catch(() => ({ data: { bills: [], totalAmount: 0 } })),
          api.get(`/hospital/admission`).catch(() => ({ data: { data: { admissions: [] } } })),
          api.get(`/hospital/bed`).catch(() => ({ data: { beds: [] } })),
          api.get('/hospital/hospital-bills/?page=1&limit=5').catch(() => ({ data: { bills: [] } }))
        ]);
        setDoctorsData(doctorsRes.data?.data || doctorsRes.data || []);
        setBillsLoading(false);
        const allAdmissions = hospitalAdmissionsRes.data?.data?.admissions || [];
        const currentAdmissions = allAdmissions.filter(admission => admission.status === 'Admitted');
        const totalCurrentAdmissions = currentAdmissions.length;
        const allBeds = hospitalBedsRes.data?.beds || [];
        const totalBeds = allBeds.length;
        const occupiedBeds = currentAdmissions.length;
        const availableBeds = totalBeds - occupiedBeds;
        setRecentHospitalBills(recentHospitalBillsRes.data?.bills || []);
        setTotalBeds(totalBeds);
        setOccupiedBeds(occupiedBeds);

        const admissionsToday = currentAdmissions.filter(admission => 
            admission.admissionDate && admission.admissionDate.startsWith(today)
        ).length;

        const admissionsThisMonth = currentAdmissions.filter(admission => {
            if (!admission.admissionDate) return false;
            const admissionDate = new Date(admission.admissionDate);
            return admissionDate.getFullYear() === year && (admissionDate.getMonth() + 1) === month;
        }).length;

        // --- NICU Bills data ---
        const todayHospitalBillsCount = todayHospitalBillsRes.data?.totalTransactions || 0;
        const todayHospitalAmount = parseFloat(todayHospitalBillsRes.data?.totalAmount) || 0;
        const thisMonthHospitalBillsCount = monthHospitalBillsRes.data?.bills?.length || 0;
        const thisMonthHospitalAmount = parseFloat(monthHospitalBillsRes.data?.totalAmount) || 0;
        const thisYearHospitalBillsCount = yearHospitalBillsRes.data?.bills?.length || 0;
        const thisYearHospitalAmount = parseFloat(yearHospitalBillsRes.data?.totalAmount) || 0;
        
        const cards = [
          {
            category: "Hospital Billing",
            icon: <FaFileInvoiceDollar className="text-purple-500 text-xl" />,
            total: todayHospitalBillsCount,
            status: "Today",
            cardListDetails: [
                { label: "Today's Amount", value: `৳${todayHospitalAmount.toLocaleString()}` },
                { label: "This Month", value: `${thisMonthHospitalBillsCount} bills (৳${thisMonthHospitalAmount.toLocaleString()})` },
                { label: "This Year", value: `${thisYearHospitalBillsCount} bills (৳${thisYearHospitalAmount.toLocaleString()})` },
            ],
            link: "/hospital-bill" 
        },
         {
            category: "Current Hospital Admissions",
            icon: <FaUsers className="text-blue-500 text-xl" />,
            total: totalCurrentAdmissions,
            status: "Admitted",
            cardListDetails: [
            { label: "Admissions Today", value: admissionsToday },
            { label: "Admissions This Month", value: admissionsThisMonth },
            ],
            link: "/hospital-patients"
        },
         {
            category: "Hospital Bed Occupancy",
            icon: <FaBed className="text-orange-500 text-xl" />,
            total: availableBeds,
            status: "Available",
            cardListDetails: [
            { label: "Total Beds", value: totalBeds },
            { label: "Occupied Beds", value: occupiedBeds },
            ],
            link: "/hospital-config"
        },
        ];
        
        setDashboardData(cards);

      } catch (error) {
        console.error("Error fetching pharmacist dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPathologistData();
  }, []);

  return (
    <div className="md:py-4">
       {/* Top Title Area */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <h1 className="font-semibold text-lg">
          Welcome back, {user?.name|| "Hospital Office Stuff"} 👋
        </h1>
        <div>
          <Link to="/hospital-bill">
            <button className="py-2 px-4 bg-[#73CB64] hover:bg-green-700 text-white rounded-md flex items-center gap-2 text-sm">
              <TbSortAscending /> Add Bill
            </button>
          </Link>
        </div>
      </div>
      <div className="py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-6">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} height={150} />
            ))
          ) : (
            dashboardData.map((card, i) => (
              <Cards key={i} data={card} isEven={i % 2 === 0} showLinkIcon={true} />
            ))
          )}
        </div>
      </div>
      {/* Main Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6">
        <TechnicalSupportNotice />
        {/* Pathology Stats */}
        <div className="col-span-2 bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">
              Monthly Performance (Admissions vs Billing)
            </h2>
            <ul className="flex gap-2">
              {["All", "1M", "6M", "1Y"].map((t) => (
                <li
                  key={t}
                  className="w-9 py-1 text-center text-sm bg-gray-100 hover:bg-gray-200 rounded-sm cursor-pointer"
                >
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-4">
            {/* Use the new chart component */}
            {loading ? <Skeleton height={400} /> : <HospitalPerformanceChart />}
        </div>
        </div>
      </div>
      {/* Top Doctors & Breakdown */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-y-6 xl:gap-y-0 xl:gap-6 mt-6">
        {/* Top Doctors */}
        <div className="col-span-2 bg-white shadow rounded-lg">
            <div className="p-4 border-b">
              <h2 className="font-medium">Top Doctors</h2>
            </div>
            <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3  gap-4">
              {loading ? (
              Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} height={120} />)
            ) : doctorsData.length > 0 ? (
              [...doctorsData]
                .sort((a, b) => {
                  const patientsB = parseInt(b.translations.en.patientAttended) || 0;
                  const patientsA = parseInt(a.translations.en.patientAttended) || 0;
                  if (patientsB !== patientsA) {
                    return patientsB - patientsA;
                  }
                  const experienceB = parseInt(b.translations.en.yearsOfExperience) || 0;
                  const experienceA = parseInt(a.translations.en.yearsOfExperience) || 0;
                  return experienceB - experienceA;
                })
                .slice(0, 6)
                .map((doc) => (
                  <TopDoctorCard key={doc.id} doctor={doc} isAdmin={false} />
                ))
            ) : (
              <p className="col-span-3 text-center text-gray-500 py-4">No doctors available.</p>
            )}
            </div>
            <div className="p-4 border-t">
              <Link to="/all-doctors">
                <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
                  See All Doctors <TiArrowRight />
                </button>
              </Link>
            </div>
          </div>

        {/* Hospital Office Breakdown */}
        <div className="bg-white shadow rounded-lg">
          <div className="flex flex-wrap gap-3 justify-between p-4 border-b">
            <h2 className="font-medium">Bed Occupancy Breakdown</h2>
          </div>
          <div className="p-5">
            {loading ? (
              <Skeleton height={400} />
            ) : (
              <HospitalBedChart
                totalBeds={totalBeds} 
                occupiedBeds={occupiedBeds} 
                isLoading={loading} 
             />
            )}
          </div>
        </div>
      </div>
       {/* Hospital BILLS TABLE SECTION --- */}
    <div className="mt-6 bg-white shadow rounded-lg">
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="font-medium">Recent Hospital Bills</h2>
        <Link to="/hospital-bill">
          <button className="px-3 py-2 bg-[#73CB64] hover:bg-M-primary-color/90 transition-all duration-300 text-white rounded-md flex items-center gap-1 text-sm">
            View All <TiArrowRight />
          </button>
        </Link>
      </div>
      <div className="p-4 overflow-x-auto">
        {billsLoading ? (
          <Skeleton height={200} />
        ) : (
          <table className="min-w-full w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Bill No</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Net Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Amount (৳)</th>
                <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {recentHospitalBills.length > 0 ? (
                    recentHospitalBills.map((bill) => (
                        <tr key={bill.id} className="hover:bg-gray-50">
                            <td className="p-3 text-sm text-gray-700">{bill.billNumber || '-'}</td>
                            <td className="p-3 text-sm text-gray-700">
                                {bill.createdAt ? new Date(bill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}
                            </td>
                            <td className="p-3 text-sm text-gray-700">{bill.netAmount?.toFixed(2) || '0.00'}</td>
                            <td className="p-3 text-sm text-gray-700">{bill.paidAmount?.toFixed(2) || '0.00'}</td>
                            <td className="p-3 text-sm text-gray-700">{user?.name || "Unknown"}</td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="5" className="text-center py-5 text-gray-500">
                            No recent Hospital bills found.
                        </td>
                    </tr>
                )}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </div>
  );
};

export default HospitalDashboard;